// Copyright (c) 2022-2023. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { explainEnum, isEnum, parseEnum, stringifyEnum } from "../../../../io/hyperify/core/types/Enum";
import { explainNot, explainOk, explainOr } from "../../../../io/hyperify/core/types/explain";
import { isUndefined } from "../../../../io/hyperify/core/types/undefined";

export enum SendanorApiPermission {

    /**
     * Enables super admins to select any client by an ID
     */
    SELECT_ANY_CLIENT = "SELECT_ANY_CLIENT",
    MODIFY_INVENTORY_CLIENT_ID = "MODIFY_INVENTORY_CLIENT_ID",
    MODIFY_INVENTORY_STORAGE = "MODIFY_INVENTORY_STORAGE",

    /**
     * If enabled, DTOs will contain full data, not just public part.
     * Including internal information.
     */
    VIEW_FULL_DTO = "VIEW_FULL_DTO",

    VIEW_TICKETS = "VIEW_TICKETS",
    CREATE_TICKET = "CREATE_TICKET",
    COMMENT_TICKET = "COMMENT_TICKET",

    VIEW_CLIENT = "VIEW_CLIENT",
    VIEW_INVOICES = "VIEW_INVOICES",
    VIEW_INVENTORY = "VIEW_INVENTORY",

    CREATE_CLIENT = "CREATE_CLIENT",
    CREATE_ORDER = "CREATE_ORDER",

    /**
     * Can save inventory item but not submit it to WAITING_APPROVAL state
     */
    SAVE_DRAFT_INVENTORY_ITEM = "DRAFT_INVENTORY_ITEM",

    /**
     * Can submit inventory item (e.g. change it as `InventoryState.WAITING_APPROVAL`)
     */
    DRAFT_INVENTORY_ITEM = "DRAFT_INVENTORY_ITEM",

    /**
     * Can change inventory state as `InventoryState.CHANGING`
     */
    APPROVE_INVENTORY_ITEM = "APPROVE_INVENTORY_ITEM",

    /**
     * Can change inventory state as `InventoryState.READY` or `InventoryState.ERROR`
     */
    RESOLVE_INVENTORY_ITEM = "RESOLVE_INVENTORY_ITEM"

}

export function isSendanorApiPermission (value: unknown) : value is SendanorApiPermission {
    return isEnum(SendanorApiPermission, value);
}

export function explainSendanorApiPermission (value : unknown) : string {
    return explainEnum("SendanorApiPermission", SendanorApiPermission, isSendanorApiPermission, value);
}

export function stringifySendanorApiPermission (value : SendanorApiPermission) : string {
    return stringifyEnum(SendanorApiPermission, value);
}

export function parseSendanorApiPermission (value: any) : SendanorApiPermission | undefined {
    return parseEnum(SendanorApiPermission, value) as SendanorApiPermission | undefined;
}

export function isSendanorApiPermissionOrUndefined (value: unknown): value is SendanorApiPermission | undefined {
    return isUndefined(value) || isSendanorApiPermission(value);
}

export function explainSendanorApiPermissionOrUndefined (value: unknown): string {
    return isSendanorApiPermissionOrUndefined(value) ? explainOk() : explainNot(explainOr(['SendanorApiPermission', 'undefined']));
}
